import React, { useEffect, useMemo, useState } from 'react';
import { Card } from 'components/shadcn/ui/card';
import { Formik, Form } from 'formik';
import getValidationSchema from 'components/HGForm/formValidations';
import {
    addSpaceBeforeEveryCapitalLetter,
    stringifyAddressObject,
    parseAddressString,
} from 'components/HGForm/helpers';
import * as Yup from 'yup';
import HGError from 'components/HGForm/HGError';
import HGFormInput from 'components/HGForm/HGFormInput';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { Auth } from 'aws-amplify';
import HGFormSubmitBtn from 'components/HGForm/HGFormSubmitBtn';
import AddressFields from 'components/HGForm/AddressFields';
import {
    useJwtToken,
    useVerifyCustomer,
    useVerifyInstaller,
} from 'helper';
import { Button } from 'components/shadcn/ui/button';
import { useNavigate } from 'react-router-dom';
import { useToast } from 'components/shadcn/hooks/use-toast';
import HGSpinner from 'components/HGSpinner';
import { updateUser } from '../service';

const EditUserProfile = () => {
    const { user } = useAuthenticator((context) => [context.user]);
    const accessToken = useJwtToken();
    const isCustomer = useVerifyCustomer();
    const isInstaller = useVerifyInstaller();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const navigate = useNavigate();
    const userAttributes = user?.attributes;
    const { toast } = useToast();
    const [initialFieldsValues, setInitialFieldsValues] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: {
            address: '',
            city: '',
            stateProvince: '',
            postalCode: '',
            country: 'United States',
        },
    });

    const fieldKeysArray = Object.keys(initialFieldsValues);

    useEffect(() => {
        if (userAttributes) {
            const addressObject = parseAddressString(userAttributes?.address);
            setInitialFieldsValues({
                firstName: userAttributes.name || '',
                lastName: userAttributes.family_name || '',
                email: userAttributes.email || '',
                phone: userAttributes.phone_number?.slice(1) || '',
                address: {
                    address: addressObject?.address || '',
                    city: addressObject?.city || '',
                    stateProvince: addressObject?.stateProvince || '',
                    postalCode: addressObject?.postalCode || '',
                    country: addressObject?.country || 'United States',
                },
            });
        }
    }, [userAttributes]);

    const validationSchema = useMemo(() => {
        const schemaShape = fieldKeysArray.reduce((acc, key) => {
            if (initialFieldsValues[key] === null) {
                return acc;
            }
            const schema = getValidationSchema(key);
            const fieldLabel = addSpaceBeforeEveryCapitalLetter(key);
            acc[key] = schema.required(`${fieldLabel} is required`);
            return acc;
        }, {});
        return Yup.object().shape(schemaShape);
    }, [fieldKeysArray, initialFieldsValues]);

    const updateUserAttributes = async (values) => {
        setErrorMessage(null);
        setIsSubmitting(true);

        const transformedValues = Object.keys(values).reduce((acc, key) => {
            // removes keys with null values
            if (!values[key]) return acc;
            if (key === 'firstName') acc.name = values[key];
            if (key === 'lastName') acc.family_name = values[key];
            if (key === 'phone') acc.phone_number = `+${values[key]}`;
            if (key === 'address') {
                const addressString = stringifyAddressObject(values[key]);
                acc.address = addressString;
            }
            return acc;
        }, {});

        let response = null;

        try {
            response = await Auth.updateUserAttributes(user, transformedValues);
            if (response === 'SUCCESS' && !isCustomer && !isInstaller) {
                toast({
                    title: 'User information updated successfully',
                });
                setIsSubmitting(false);
                navigate('/profile');
            }
        } catch (error) {
            console.error('Error updating user attributes: ', error);
            setErrorMessage(error.message);
            setIsSubmitting(false);
        }
        if (response === 'SUCCESS' && (isCustomer || isInstaller)) {
            const userInfo = {
                userId: userAttributes?.sub,
                email: userAttributes?.email,
                companyId: userAttributes?.['custom:company_id'],
                ...transformedValues,
            };
            const updatedUser = await updateUser(
                userAttributes?.['custom:role'],
                userInfo,
                accessToken
            );

            if (updatedUser) {
                toast({
                    title: 'User information updated successfully',
                });
                setIsSubmitting(false);
                navigate('/profile');
            } else {
                setErrorMessage(
                    `Error updating user, please try again or contact support.`
                );
                setIsSubmitting(false);
            }
        }
    };

    if (!userAttributes?.email) {
        return null;
    }

    return (
        <Card className="space-y-5 p-5">
            <h4 className="mb-3 text-lg font-semibold">Edit User Profile</h4>

            <Formik
                initialValues={initialFieldsValues}
                validationSchema={validationSchema}
                onSubmit={(value) => updateUserAttributes(value)}
                validateOnBlur={false}
                enableReinitialize
            >
                <Form className="relative">
                    <div className="my-5 grid grid-cols-1 gap-5 md:grid-cols-2">
                        {fieldKeysArray.map((key) => {
                            if (
                                !initialFieldsValues[key]?.length ||
                                key === 'address'
                            )
                                return null;
                            return (
                                <HGFormInput
                                    key={key}
                                    name={key}
                                    label={addSpaceBeforeEveryCapitalLetter(
                                        key
                                    )}
                                    variant="standard"
                                    disabled={key === 'email'}
                                />
                            );
                        })}
                    </div>
                    {initialFieldsValues['address']?.address && (
                        <AddressFields />
                    )}
                    {isSubmitting && <HGSpinner />}
                    <div className="mt-5 flex justify-end gap-2">
                        <Button
                            variant="outline"
                            type="button"
                            onClick={() => navigate('/profile')}
                        >
                            Cancel
                        </Button>
                        <HGFormSubmitBtn loading={isSubmitting}>
                            Save
                        </HGFormSubmitBtn>
                    </div>
                    {errorMessage && <HGError errorMessage={errorMessage} />}
                </Form>
            </Formik>
        </Card>
    );
};

export default EditUserProfile;
